import { BaseballLineupPosition, DraggableList, IApiLineupPlayer } from '@trackman/baseball-shared-components';
import EditLineupModal from 'components/modals/EditLineupModal/EditLineupModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ReactComponent as CatcherIcon } from 'images/catcherIcon.svg';
import { ReactComponent as PitcherIcon } from 'images/pitcherIcon.svg';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { IPlayer } from 'interfaces/team/IPlayer';
import { observer } from 'mobx-react-lite';
import { DragIndicator } from 'pages/SetLineup/components/TeamSetup/TeamLineup/TeamLineupStyles';
import React, { useEffect, useRef, useState } from 'react';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { useInjection } from 'services/ioc/ioc.react';
import { SessionStore } from 'stores/SessionStore';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { EditLineupMenu } from '../../EditLineupMenu/EditLineupMenu';
import { SectionHeader } from '../../SectionHeader/SectionHeader';
import { SectionHeadline } from '../../SectionHeadline/SectionHeadline';
import { LineupBatter } from '../LineupBatter/LineupBatter';
import { LineupPitcher } from '../LineupPitcher/LineupPitcher';
import { Substitutions } from '../Substitutions/Substitutions';
import { LineupListWrapper, TaggingColumnWrapper } from './LineupListStyles';

interface IProps {
  disabled?: boolean;
}

const LineupList: React.FC<IProps> = observer(({ disabled }) => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);

  if (!taggingStore.isInitialized) {
    return null;
  }

  const [showPitchers, setShowPitchers] = useState<boolean>(true);
  const [showBatters, setShowBatters] = useState<boolean>(true);
  const home = 'home';
  const away = 'away';
  const [editModalForTeam, setEditModalForTeam] = useState<string>();
  const [substitutionPlayer, setSubstitutionPlayer] = useState<IApiLineupPlayer>();
  const ref = useRef<any>(null);
  const [refHeight, setRefHeight] = useState<number>(ref.current && ref.current.clientHeight);
  const { playStore } = taggingStore;
  const { selectedPlay, placeholderPlay } = playStore;
  const displayPlay = selectedPlay || placeholderPlay;
  const isReadOnlyMode = !selectedPlay || disabled;
  const battingTeamLineup = displayPlay.getBattingTeamLineup().players;
  const batterList = displayPlay.getBattingTeamLineup().getBatterList();
  const teamPitcher = displayPlay.getPitchingTeamLineup().getPlayer(BaseballLineupPosition.Pitcher);
  const pitchingTeamLineup = displayPlay.getPitchingTeamLineup().players;
  const [currentBatterId, setCurrentBatterId] = useState<string>(batterList[0]?.id);

  if (batterList.length === 0) {
    window.location.reload();
    return null;
  }

  useEffect(() => {
    setRefHeight(ref.current.clientHeight);
  }, [ref.current && ref.current.clientHeight]);

  useEffect(() => {
    setShowPitchers(true);
    setShowBatters(true);
  }, [playStore.selectedPlay!]);

  useEffect(() => {
    setCurrentBatterId(selectedPlay ? selectedPlay.batter.id : batterList[0]?.id);
  }, [selectedPlay, selectedPlay && selectedPlay.batter]);

  useEffect(() => {
    taggingStore.disableEnableHotkeys(!!editModalForTeam);
  }, [editModalForTeam, taggingStore]);

  const onDropCompleted = (items: ApiLineupDraggablePlayer[]): void => {
    playStore.updateLineupOrder(displayPlay, items);
    if (!selectedPlay) {
      setCurrentBatterId(items[0].id);
    }
  };

  const changePlayerOnSelectedPlay = (playerId: string, lineup: IApiLineupPlayer[], isPitcherSubstitution: boolean): void => {
    if (isReadOnlyMode) {
      return;
    }

    const player = lineup.find((x) => x.id === playerId);

    if (playStore.isPlaceholderPlaySelected && player) {
      playStore.manuallyChangePlayerAtBat(player);
    }

    if (selectedPlay && player) {
      if (isPitcherSubstitution) {
        selectedPlay.pitcher = { ...player };
      } else {
        selectedPlay.batter = { ...player };
      }
      selectedPlay.isChanged = true;
    }
  };

  const handleOnClickPitcherSubstitution = (player?: IApiLineupPlayer): void => {
    if (isReadOnlyMode) {
      return;
    }
    setShowPitchers(!showPitchers);
    setShowBatters(!showBatters);
    setSubstitutionPlayer(player);
  };

  const handleOnClickBatterSubstitution = (e?: React.SyntheticEvent, player?: IApiLineupPlayer): void => {
    if (e) {
      e.stopPropagation();
    }
    if (isReadOnlyMode) {
      return;
    }
    setShowBatters(!showBatters);
    setSubstitutionPlayer(player);
  };

  const handleOnSubstitution = (isPitcher: boolean, player: IPlayer): void => {
    const selectedBatterId = selectedPlay?.batter.id;
    playStore.substitutePlayer(displayPlay, substitutionPlayer!.id, player, isPitcher);

    if (isPitcher) {
      setShowPitchers(!showPitchers);
      changePlayerOnSelectedPlay(player.id, pitchingTeamLineup, isPitcher);
    } else {
      setShowBatters(!showBatters);
      if (substitutionPlayer && selectedBatterId === substitutionPlayer.id) {
        changePlayerOnSelectedPlay(player.id, battingTeamLineup, isPitcher);
      }
    }
    if (selectedPlay) {
      selectedPlay.isChanged = true;
    }

    setShowBatters(!showBatters);
  };

  const renderItem = (item: IApiLineupPlayer, index: number, isDragging: boolean, dragRef: any): JSX.Element => {
    return (
      <>
        <DragIndicator style={{ display: isDragging ? 'block' : 'none' }} />
        <div style={{ opacity: isDragging ? 0 : 1 }}>
          <LineupBatter
            key={index}
            player={item}
            drag={dragRef}
            isCurrent={item.id === currentBatterId}
            index={(index + 1).toString()}
            onClickPlayerCallback={(playerId: string): void => changePlayerOnSelectedPlay(playerId, batterList, false)}
            onClickSubstitutionCallback={(e: React.SyntheticEvent, player: IApiLineupPlayer): void =>
              handleOnClickBatterSubstitution(e, player)
            }
            disabled={isReadOnlyMode}
          />
        </div>
      </>
    );
  };

  const renderPreview = (item: IApiLineupPlayer, index: number): JSX.Element => {
    return (
      <LineupBatter
        key={index}
        player={item}
        drag={undefined}
        isCurrent={item.id === currentBatterId}
        index={(index + 1).toString()}
        onClickPlayerCallback={(): void => {}}
        onClickSubstitutionCallback={(): void => {}}
        disabled={isReadOnlyMode}
      />
    );
  };

  const renderLineupList = (): JSX.Element => {
    let renderPitchers = (
      <>
        <SectionHeader icon={<CatcherIcon />} title={(displayPlay.getPitchingTeam(sessionStore.currentSessionMetadata)?.name ?? '')} />
        <LineupPitcher
          player={teamPitcher}
          onClickSubstitutionCallback={(player: IApiLineupPlayer): void => handleOnClickPitcherSubstitution(player)}
          disabled={isReadOnlyMode}
        />
      </>
    );
    let renderBatters = (
      <>
        <SectionHeader icon={<PitcherIcon />} title={(displayPlay.getBattingTeam(sessionStore.currentSessionMetadata)?.name ?? '')} />
        <DraggableList items={batterList} onDropCompleted={onDropCompleted} renderItem={renderItem} renderPreview={renderPreview} />
      </>
    );
    if (showPitchers && !showBatters) {
      renderPitchers = (
        <>
          <SectionHeader title={(displayPlay.getPitchingTeam(sessionStore.currentSessionMetadata)?.name ?? '')} />
          <LineupPitcher
            player={teamPitcher}
            onClickSubstitutionCallback={(player: IApiLineupPlayer): void => {}}
            disabled={isReadOnlyMode}
          />
        </>
      );
      renderBatters = (
        <Substitutions
          play={displayPlay}
          refHeight={refHeight}
          onClickBackCallback={handleOnClickBatterSubstitution}
          substitutionPlayer={substitutionPlayer}
          onClickPlayerCallback={(player: IPlayer): void => handleOnSubstitution(false, player)}
        />
      );
    } else if (!showPitchers && !showBatters) {
      renderPitchers = (
        <Substitutions
          play={displayPlay}
          isPitcherSubstitution
          refHeight={refHeight}
          onClickBackCallback={handleOnClickPitcherSubstitution}
          substitutionPlayer={substitutionPlayer}
          onClickPlayerCallback={(player: IPlayer): void => handleOnSubstitution(true, player)}
        />
      );
      renderBatters = <></>;
    }
    return (
      <div ref={ref} style={{ height: '100%' }}>
        <div className='pitcher'>{renderPitchers}</div>
        <div className='batters'>{renderBatters}</div>
      </div>
    );
  };

  const isEditingBattingLineup = (): boolean => {
    return (editModalForTeam === home && displayPlay.isHomeTeamBatting) || (editModalForTeam === away && !displayPlay.isHomeTeamBatting);
  };

  const editLineup = async (newLineup: ApiLineupDraggablePlayer[]): Promise<void> => {
    await playStore.updateLineup(displayPlay, isEditingBattingLineup(), newLineup, batterList);
    setEditModalForTeam(undefined);
  };

  return (
    <LineupListWrapper>
      <EditLineupModal
        isVisible={!!editModalForTeam}
        onDismiss={(): void => setEditModalForTeam(undefined)}
        changeLineupAsync={editLineup}
        currentLineup={isEditingBattingLineup() ? battingTeamLineup : pitchingTeamLineup}
        roster={editModalForTeam === home ? taggingStore.homeTeamRoster : taggingStore.awayTeamRoster}
        team={mappingHelpers.teamWithLineupToTeamMap(
          editModalForTeam === home ? sessionStore.currentSessionMetadata.homeTeam : sessionStore.currentSessionMetadata.awayTeam
        )}
        ruleset={sessionStore.currentSessionMetadata.ruleSet}
        unavailablePlayerIds={isEditingBattingLineup() ? pitchingTeamLineup.map((p) => p.id) : battingTeamLineup.map((p) => p.id)}
      />
      <SectionHeadline>
        Lineup{' '}
        <EditLineupMenu
          homeTeamName={sessionStore.currentSessionMetadata.homeTeam?.name ?? ''}
          awayTeamName={sessionStore.currentSessionMetadata.awayTeam?.name ?? ''}
          onOptionSelected={(isHomeTeam: boolean): void => setEditModalForTeam(isHomeTeam ? home : away)}
        />
      </SectionHeadline>
      <TaggingColumnWrapper className='tagging-column'>{renderLineupList()}</TaggingColumnWrapper>
    </LineupListWrapper>
  );
});

export default LineupList;
